import { ArrowLeft, ArrowRight, X, Sparkles, Palette, LayoutGrid } from 'lucide-react';
import { useOnboardingStore, UploadedMethodType } from '../../store/onboardingStore';
import { invalidateWebsitesCache } from '../../store/websitesStore';
import { StepProductData } from './steps/StepProductData';
import { StepConfirmScrapedProduct } from './steps/StepConfirmScrapedProduct';
import { ConfirmScrapedProductView } from './ui/ConfirmScrapedProductView';
import { StepAdditionalOffers } from './steps/StepAdditionalOffers';
import { StepBrandAndPrice } from './steps/StepBrandAndPrice';
// StepFunnelCount removed — funnel count is now determined by template selection in StepFunnelConfig
import { StepFunnelConfig } from './steps/StepFunnelConfig';
import { StepFunnelSummary } from './steps/StepFunnelSummary';
import { dictionary, Language } from '../../utils/dictionary';
import { useAuth } from '../../context/AuthContext';
import toast, { Toaster } from 'react-hot-toast';
import { useEffect, useRef, useState } from 'react';
import { axiosInstanceConversationalEngine } from '../../services/instance';
import { MultiFunnelItem, MultiFunnelPayload, MultiFunnelProduct } from '../../services/interface';
import { postBulkFunnels } from '../../services/funnels';
import { creativesService } from '../../feature/ads/cretives/services/creatives.service';
import { userExperimentsService } from '../../services/userExperiments/userExperimentsService';
import { Clock, Users, Shield, Zap, Leaf } from 'lucide-react';
import Loader from '../common/Loader';
import { useTutorialContext } from '../../context/TutorialContext';
import { useNavigate } from 'react-router-dom';
import { Chip } from '../common/chip/Chip';
import { ModalWrapper } from '../modals/ModalWrapper';
import { BuyCreditsModal } from '../modals/BuyCreditsModal';
import { membershipStatsAxios } from '../../services/membership/MembershipAxios';
import { buildTemplateStudioPages } from '../../templateStudio/onboardingTemplateCompiler';
import { getSubmittablePromotion } from '../../utils/promotions';
// import { useTutorialContext } from '../../context/TutorialContext';
// import { TutorialSetup } from '../TutorialSetup';
// import WelcomeModal from '../WelcomeModal';
// import TutorialTooltip from '../TutorialTooltip';


const customToast = (message: string, title: string = 'No pudimos cargar información de la página') => {
  return toast.custom(
    (t) => (
      <div
        className="max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5"
        style={{
          opacity: t.visible ? 1 : 0,
          transform: t.visible ? 'translateX(0)' : 'translateX(100%)',
          transition: 'all 0.3s ease-out',
        }}
      >
        <div className="p-4 w-full flex items-center">
          <div className="flex-shrink-0">
            <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center">
              <X className="h-5 w-5 text-red-500" />
            </div>
          </div>
          <div className="ml-3 flex-1">
            <p className="text-lg font-medium text-gray-900">
              {title}
            </p>
            <p className="mt-1 text-sm text-gray-600">
              {message}
            </p>
          </div>
        </div>
      </div>
    ),
    {
      duration: 4000,
      position: 'top-right',
    }
  );
};

const stableStringify = (value: any): string => {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  return `{${Object.keys(value)
    .filter(key => value[key] !== undefined)
    .sort()
    .map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
    .join(',')}}`;
};

const hashString = (value: string): string => {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const createOnboardingRequestSessionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const buildBulkClientRequestId = (sessionId: string, payload: MultiFunnelPayload) => {
  return `${sessionId}:${hashString(stableStringify(payload))}`;
};

let activeOnboardingLayouts = 0;
const SALES_ANGLES_TIMEOUT_MS = 420_000;

export function OnboardingLayout() {
  const { data, setStep, nextStep, previousStep, updateProductData, updateVisualContent, setSalesAngles, updateAdditionalOffers, updateContextData, contextData, hasFetchedSalesAngles, setHasFetchedSalesAngles, resetOnboardingData, setPriceErrors, clearPriceErrors, setShowCustomSectionsToggle, multiFunnelData, isFetchingDesignRecommendation, isFetchingSalesAngleRecommendation, estimatedCost, selectedLegacyTemplates, salesAngles } = useOnboardingStore();
  const { productData } = data;
  const [isLoading, setIsLoading] = useState(false);
  const [funnelSubStep, setFunnelSubStep] = useState(1);
  const { language, country } = useAuth();
  const { step } = data;
  const [productCompleteness, setProductCompleteness] = useState<any>(data.additionalOffers);
  const [showConfirmScrapedProduct, setShowConfirmScrapedProduct] = useState(false);
  const [showDesignAnalysis] = useState(false);
  type ScrapedConfirmItem = 'bump' | { type: 'upsell'; index: number };
  const [scrapedConfirmQueueStep2, setScrapedConfirmQueueStep2] = useState<ScrapedConfirmItem[]>([]);
  const { resumeTutorial, setCurrentStepIndex, isPaused } = useTutorialContext();
  const navigate = useNavigate();
  const [onboardingRequestSessionId] = useState(createOnboardingRequestSessionId);
  const [isOpenBuyCreditsModal, setIsOpenBuyCreditsModal] = useState(false);
  const [userCredits, setUserCredits] = useState(0);
  const [endMembershipDate, setEndMembershipDate] = useState("");
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [isFetchingSalesAngles, setIsFetchingSalesAngles] = useState(false);
  const [salesAnglesError, setSalesAnglesError] = useState<string | null>(null);
  const salesAnglesRequestRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    activeOnboardingLayouts += 1;

    return () => {
      activeOnboardingLayouts -= 1;
      window.setTimeout(() => {
        if (activeOnboardingLayouts === 0) {
          useOnboardingStore.getState().resetOnboardingData();
        }
      }, 0);
    };
  }, []);

  useEffect(() => {
    setLoadingCredits(true);
    membershipStatsAxios().then((res) => {
      if (res?.active_membership) {
        setUserCredits(res.active_membership.credits);
        setEndMembershipDate(res.active_membership.ends_at);
      }
      setLoadingCredits(false);
    }).catch(() => setLoadingCredits(false));
  }, []);

  // Design analysis screen removed — funnels are now initialized inside StepFunnelConfig

  // Design recommendation is fetched when moving from step 3 → 4 (product confirmed).
  // NOT on mount — product data isn't ready yet at mount time.

  useEffect(() => {
    if (step === 1 && isPaused) {
      setCurrentStepIndex(1);
      resumeTutorial();
    }
  }, [step]);

  useEffect(() => {
    setProductCompleteness(data.additionalOffers);
  }, [data.additionalOffers]);

  useEffect(() => {
    const hasPrincipalProduct =
      Boolean(data.productData.name?.trim()) &&
      Boolean(data.productData.details?.trim()) &&
      ((data.visualContent.images?.length ?? 0) > 0 || (data.visualContent.imagesBase64?.length ?? 0) > 0);

    if (step > 1 && !hasPrincipalProduct) {
      setStep(1);
      customToast(
        dictionary["product-information-incomplete"]?.[language],
        dictionary["product-information-incomplete-title"]?.[language]
      );
    }
  }, [
    step,
    data.productData.name,
    data.productData.details,
    data.visualContent.images?.length,
    data.visualContent.imagesBase64?.length,
    language,
    setStep,
  ]);

  useEffect(() => {
    if (step !== 2) setScrapedConfirmQueueStep2([]);
  }, [step]);

  useEffect(() => {
    const fetchExperiments = async () => {
      try {
        const response = await userExperimentsService.getExperiments();
        const websiteToggle = response.experiments?.website_creation_toggle;
        if (websiteToggle?.enabled === "true") {
          setShowCustomSectionsToggle(true);
        }
      } catch (error) {
        console.error("Error fetching user experiments:", error);
      }
    };
    fetchExperiments();
  }, []);

  // const {
  //   isWelcomeOpen,
  //   isTutorialActive,
  //   currentStep: tutorialStep,
  //   startTutorial,
  //   nextStep: nextTutorialStep,
  //   closeTutorial
  // } = useTutorialContext();

  const currentConfirmStep2 = scrapedConfirmQueueStep2[0];

  const renderStepContent = () => {
    // Show AI design analysis screen between step 4b → 5b while recommendation loads
    if (showDesignAnalysis) {
      return <DesignAnalysisScreen language={language} />;
    }

    if (step === 1 && showConfirmScrapedProduct) {
      return (
        <StepConfirmScrapedProduct
          onBack={() => {
            // Reset everything so user starts fresh with a new product
            setShowConfirmScrapedProduct(false);
            updateProductData({
              ...data.productData,
              productLink: '',
              name: '',
              details: '',
              price: '',
              externalId: null,
              providerId: null,
              variants: null,
              isManualEntry: false,
            });
            updateVisualContent({ images: [], imagesBase64: [] });
            updateAdditionalOffers({
              ...data.additionalOffers,
              bumpOffer: undefined,
              upsell: { ...data.additionalOffers.upsell, enabled: false, products: [] },
            });
          }}
          onConfirm={() => {
            setShowConfirmScrapedProduct(false);
            fetchSalesAngles();
            nextStep();
          }}
        />
      );
    }

    if (step === 2 && currentConfirmStep2) {
      const bumpOffer = data.additionalOffers.bumpOffer;
      const upsellProducts = data.additionalOffers.upsell?.products ?? [];

      if (currentConfirmStep2 === 'bump' && bumpOffer) {
        const bumpImages = bumpOffer.url_images ? [bumpOffer.url_images] : [];
        return (
          <ConfirmScrapedProductView
            title={dictionary['confirm-order-bump']?.[language] ?? 'Confirma tu Order Bump'}
            subtitle={dictionary['edit-data-if-needed']?.[language]}
            name={bumpOffer.productName || ''}
            description={bumpOffer.description || ''}
            images={bumpImages}
            onNameChange={(value) =>
              updateAdditionalOffers({
                ...data.additionalOffers,
                bumpOffer: { ...bumpOffer, productName: value },
              })
            }
            onDescriptionChange={(value) =>
              updateAdditionalOffers({
                ...data.additionalOffers,
                bumpOffer: { ...bumpOffer, description: value },
              })
            }
            onImagesUpdate={(images) =>
              updateAdditionalOffers({
                ...data.additionalOffers,
                bumpOffer: { ...bumpOffer, url_images: images[0] ?? '' },
              })
            }
            onBack={() => {
              setScrapedConfirmQueueStep2([]);
              updateAdditionalOffers({
                ...data.additionalOffers,
                bumpOffer: {
                  ...bumpOffer,
                  productName: '',
                  description: '',
                  url_images: '',
                  image: '',
                  externalId: null,
                  providerId: null,
                  isManualEntry: false,
                  uploadedMethod: UploadedMethodType.LINK,
                },
                upsell: {
                  ...data.additionalOffers.upsell!,
                  products: upsellProducts.map((p) => ({
                    ...p,
                    productName: '',
                    description: '',
                    url_images: [],
                    image: undefined,
                    externalId: null,
                    providerId: null,
                    isManualEntry: false,
                    uploadedMethod: UploadedMethodType.LINK,
                  })),
                },
              });
            }}
            onConfirm={() => {
              const nextQueue = scrapedConfirmQueueStep2.slice(1);
              setScrapedConfirmQueueStep2(nextQueue);
              if (nextQueue.length === 0) nextStep();
            }}
            maxImages={1}
          />
        );
      }

      if (currentConfirmStep2 !== 'bump' && upsellProducts[currentConfirmStep2.index]) {
        const upsellIndex = currentConfirmStep2.index;
        const product = upsellProducts[upsellIndex];
        const productImages = product.url_images ?? [];
        const imagesArray = Array.isArray(productImages) ? productImages : [productImages].filter(Boolean);
        return (
          <ConfirmScrapedProductView
            title={dictionary['confirm-upsell']?.[language] ?? 'Confirma tu Upsell'}
            subtitle={dictionary['edit-data-if-needed']?.[language]}
            name={product.productName || ''}
            description={product.description || ''}
            images={imagesArray}
            onNameChange={(value) => {
              const next = [...upsellProducts];
              next[upsellIndex] = { ...product, productName: value };
              updateAdditionalOffers({
                ...data.additionalOffers,
                upsell: { ...data.additionalOffers.upsell!, products: next },
              });
            }}
            onDescriptionChange={(value) => {
              const next = [...upsellProducts];
              next[upsellIndex] = { ...product, description: value };
              updateAdditionalOffers({
                ...data.additionalOffers,
                upsell: { ...data.additionalOffers.upsell!, products: next },
              });
            }}
            onImagesUpdate={(images) => {
              const next = [...upsellProducts];
              next[upsellIndex] = { ...product, url_images: images };
              updateAdditionalOffers({
                ...data.additionalOffers,
                upsell: { ...data.additionalOffers.upsell!, products: next },
              });
            }}
            onBack={() => {
              setScrapedConfirmQueueStep2([]);
              updateAdditionalOffers({
                ...data.additionalOffers,
                bumpOffer: data.additionalOffers.bumpOffer
                  ? {
                      ...data.additionalOffers.bumpOffer,
                      productName: '',
                      description: '',
                      url_images: '',
                      image: '',
                      externalId: null,
                      providerId: null,
                      isManualEntry: false,
                      uploadedMethod: UploadedMethodType.LINK,
                    }
                  : undefined,
                upsell: {
                  ...data.additionalOffers.upsell!,
                  products: upsellProducts.map((p) => ({
                    ...p,
                    productName: '',
                    description: '',
                    url_images: [],
                    image: undefined,
                    externalId: null,
                    providerId: null,
                    isManualEntry: false,
                    uploadedMethod: UploadedMethodType.LINK,
                  })),
                },
              });
            }}
            onConfirm={() => {
              const nextQueue = scrapedConfirmQueueStep2.slice(1);
              setScrapedConfirmQueueStep2(nextQueue);
              if (nextQueue.length === 0) nextStep();
            }}
          />
        );
      }
    }

    switch (step) {
      case 1:
        return <StepProductData isScraping={isLoading} />;
      case 2:
        return <StepAdditionalOffers isScraping={isLoading} />;
      case 3:
        return <StepBrandAndPrice />;
      case 4:
        return (
          <StepFunnelConfig
            onComplete={handleContinue}
            onBack={previousStep}
            onSubStepChange={setFunnelSubStep}
            isFetchingSalesAngles={isFetchingSalesAngles}
            salesAnglesError={salesAnglesError}
            onRetrySalesAngles={() => fetchSalesAngles()}
          />
        );
      case 5:
        return <StepFunnelSummary />;
      default:
        return <StepProductData />;
    }
  };

  const handleProductScrape = async () => {
    if (!data.productData.productLink) {
      return true;
    }

    setIsLoading(true);
    let success = false;

    try {
      const response = await axiosInstanceConversationalEngine.post('/scrape-product', {
        product_url: data.productData.productLink,
        country: country
      });

      const productData = response.data.data;

      const hasName = productData.name && productData.name !== "unknown";
      const hasDescription = productData.description && productData.description !== "unknown";
      const hasImages = productData.images && productData.images.length > 0;

      if (!hasName && !hasDescription && !hasImages) {
        customToast(dictionary["product-not-found"][language], dictionary["product-not-found-title"][language]);
        return false;
      }

      let variants = null;
      if (productData.provider_id === 'dropi') {
        variants = productData.variants || null;
      }

      updateProductData({
        ...data.productData,
        name: hasName ? productData.name : '',
        price: '',  // user must enter price — scraped price is supplier's, not selling price
        details: hasDescription ? productData.description : '',
        isManualEntry: false,
        externalId: productData.external_id,
        providerId: productData.provider_id,
        variants: variants
      });

      if (productData.images && productData.images.length > 0) {
        updateVisualContent({
          images: productData.images
        });
      }

      success = true;
    } catch (err) {
      console.error('Error fetching product:', err);
      customToast(dictionary["failed-to-fetch-product-data"][language], dictionary["failed-to-fetch-product-data-title"][language]);
      success = false;
    } finally {
      setIsLoading(false);
    }

    return success;
  };

  const maxStep = 5;
  const isLastStep = step === maxStep;

  const requiredCredits = estimatedCost?.total_cost ?? (multiFunnelData.funnels.length * 85);

  const validateCredits = (): boolean => {
    if (userCredits < requiredCredits) {
      setIsOpenBuyCreditsModal(true);
      return false;
    }
    return true;
  };

  const handleCreditsRefreshed = (newCredits: number) => {
    setUserCredits(newCredits);
  };

  const handleProductCompleteness = (data: any) => {
    updateAdditionalOffers(data);
    setProductCompleteness(data);
  }

  interface FetchSalesAnglesProps {
    productName?: string;
    productDescription?: string;
  }

  const fetchSalesAngles = async ({ productName = '', productDescription = '' }: FetchSalesAnglesProps = {}) => {
    const currentState = useOnboardingStore.getState();
    if (currentState.salesAngles.length > 0 && currentState.hasFetchedSalesAngles) {
      setIsLoading(false);
      setSalesAnglesError(null);
      return;
    }

    if (salesAnglesRequestRef.current) {
      return salesAnglesRequestRef.current;
    }

    const requestedProductName = currentState.data.productData?.name || productData?.name || productName;
    const requestedProductDescription = currentState.data.productData?.details || productData?.details || productDescription;

    const request = (async () => {
      setIsFetchingSalesAngles(true);
      setSalesAnglesError(null);
      setHasFetchedSalesAngles(false);

      try {
        const response = await axiosInstanceConversationalEngine.post('/resolve-info-funnel', {
          product_name: requestedProductName,
          product_description: requestedProductDescription,
          language: language,
        }, { timeout: SALES_ANGLES_TIMEOUT_MS });

        const currentProduct = useOnboardingStore.getState().data.productData;
        if (
          (currentProduct.name || '') !== (requestedProductName || '') ||
          (currentProduct.details || '') !== (requestedProductDescription || '')
        ) {
          return;
        }

        if (!response.data || !Array.isArray(response.data.sales_angles) || response.data.sales_angles.length === 0) {
          throw new Error('No sales angles returned by resolve-info-funnel');
        }

        const apiSalesAngles = response.data.sales_angles.map((angle: any, index: number) => {
          const iconMap = [Clock, Users, Shield, Zap, Leaf];
          const id = angle.name ? angle.name.toLowerCase().replace(/\s+/g, '-') : `angle-${index}`;

          return {
            id,
            title: angle.name || `Angle ${index + 1}`,
            description: angle.description || 'No description available',
            icon: iconMap[index % iconMap.length],
            isRecommended: index === 0
          };
        });

        updateContextData({
          pain_detection: response.data.pain_detection || '',
          buyer_detection: response.data.buyer_detection || '',
          recommended_sales_angles: response.data.sales_angles || [],
        });

        setSalesAngles(apiSalesAngles);
        setHasFetchedSalesAngles(true);

        // Fire AI angle selection — raw angles from API have { name, description }
        // LEGACY DISABLED: sales_angle_selector made an extra LangChain call
        // after sales_angles_v2. The new /resolve-info-funnel returns angles
        // already ordered by priority, so the first one is the default.
      } catch (error) {
        console.error("Error fetching sales angles:", error);
        const currentProduct = useOnboardingStore.getState().data.productData;
        const status = (error as any)?.response?.status;
        const isGatewayTimeout = status === 504;
        if (
          (currentProduct.name || '') !== (requestedProductName || '') ||
          (currentProduct.details || '') !== (requestedProductDescription || '')
        ) {
          return;
        }
        setSalesAngles([]);
        updateContextData({
          pain_detection: 'FUNNEL_RESEARCH_FAILED',
          buyer_detection: 'No pudimos realizar la investigación del producto.',
          recommended_sales_angles: [],
        });
        const errorMessage = isGatewayTimeout
          ? (dictionary["funnel-research-error-description"]?.[language] ??
              'El análisis del producto tomó más de lo esperado. Inténtalo nuevamente en unos minutos.')
          : (dictionary["sales-angles-error-message"]?.[language] ??
              "No pudimos generar ángulos de venta de calidad. Intenta de nuevo.");
        setSalesAnglesError(errorMessage);
        customToast(
          errorMessage,
          dictionary["funnel-research-error-title"]?.[language] ?? 'Investigación no disponible'
        );
        setHasFetchedSalesAngles(true);
      } finally {
        setIsLoading(false);
        setIsFetchingSalesAngles(false);
        salesAnglesRequestRef.current = null;
      }
    })();

    salesAnglesRequestRef.current = request;
    return request;
  };

  const handleContinue = async () => {
    if (step === 1) {
      if (data.productData.productLink &&
        (!data.productData.name || !data.productData.details || !data.visualContent.images?.length)) {
        const scrapingSuccessful = await handleProductScrape();

        if (!scrapingSuccessful) return;
        setShowConfirmScrapedProduct(true);
        return;
      }

      if (!data.productData.name || !data.productData.details || (!data.visualContent.images?.length && !data.visualContent.imagesBase64?.length)) {
        customToast(dictionary["product-information-incomplete"][language], dictionary["product-information-incomplete-title"][language]);
        return;
      }

      fetchSalesAngles();
      nextStep();
    } else if (step === 2) {
      const shouldScrapeBumpOffer = data.additionalOffers.bumpOffer?.enabled && data.additionalOffers.bumpOffer?.productLink && data.additionalOffers.bumpOffer?.uploadedMethod === UploadedMethodType.LINK;

      const upsellEnabled = data.additionalOffers.upsell?.enabled &&
        data.additionalOffers.upsell.products?.some(p =>
          p.productLink || (p.productName && p.price && p.description && p.image)
        );


      setIsLoading(true);

      try {
        const promises = [];

        if (shouldScrapeBumpOffer &&
          (!data.additionalOffers.bumpOffer?.isManualEntry ||
            (!data.additionalOffers.bumpOffer.productName &&
              !data.additionalOffers.bumpOffer.price &&
              !data.additionalOffers.bumpOffer.description))) {

          const bumpOfferPromise = axiosInstanceConversationalEngine.post('/scrape-product', {
            product_url: data.additionalOffers.bumpOffer?.productLink || ''
          }).then(response => {
            return { type: 'bumpOffer', data: response.data.data };
          });

          promises.push(bumpOfferPromise);
        }

        if (upsellEnabled) {
          const upsellPromises = data.additionalOffers.upsell.products
            .filter(product => product.productLink && !product.isManualEntry)
            .map((product, index) => {
              return axiosInstanceConversationalEngine.post('/scrape-product', {
                product_url: product.productLink
              }).then(response => {
                return {
                  type: 'upsell',
                  index: index,
                  originalProduct: product,
                  data: response.data.data
                };
              });
            });

          promises.push(...upsellPromises);
        }

        const results = await Promise.all(promises);

        const updatedAdditionalOffersNew = { ...data.additionalOffers };
        const updatedUpsellProducts = [...data.additionalOffers.upsell.products];

        results.forEach(result => {
          if (result.type === 'bumpOffer' && data.additionalOffers.bumpOffer) {
            const productData = result.data;
            updatedAdditionalOffersNew.bumpOffer = {
              ...data.additionalOffers.bumpOffer,
              productName: productData.name || '',
              price: productData.external_sell_price ? productData.external_sell_price.toString() : '0',
              description: productData.description && productData.description !== 'unknown' ? productData.description : '',
              url_images: productData.images && productData.images.length > 0 ? productData.images[0] : '',
              image: '',
              externalId: productData.external_id || null,
              providerId: productData.provider_id || null,
              isManualEntry: false
            };
          } else if (result.type === 'upsell') {
            const productData = result.data;
            updatedUpsellProducts[(result as any).index] = {
              ...(result as any).originalProduct,
              productName: productData.name,
              price: productData.external_sell_price.toString(),
              description: productData.description && productData.description !== 'unknown' ? productData.description : '',
              image: '',
              url_images: productData.images?.[0] ? [productData.images[0]] : [],
              externalId: productData.external_id || null,
              providerId: productData.provider_id || null,
              isManualEntry: false
            };
          }
        });

        if (upsellEnabled) {
          updatedAdditionalOffersNew.upsell = {
            ...data.additionalOffers.upsell,
            products: updatedUpsellProducts
          };
        }

        handleProductCompleteness(updatedAdditionalOffersNew);

        const confirmQueue: ScrapedConfirmItem[] = [];
        results.forEach((result: any) => {
          if (result.type === 'bumpOffer') confirmQueue.push('bump');
          if (result.type === 'upsell') confirmQueue.push({ type: 'upsell', index: result.index });
        });

        if (confirmQueue.length > 0) {
          setScrapedConfirmQueueStep2(confirmQueue);
          setIsLoading(false);
          return;
        }

        if (updatedAdditionalOffersNew.bumpOffer?.enabled) {
          const bumpOffer = updatedAdditionalOffersNew.bumpOffer;
          if (!bumpOffer.productName || !bumpOffer.description || (!bumpOffer.image && !bumpOffer.url_images)) {
            customToast(dictionary["order-bump-information-incomplete"][language]);
            setIsLoading(false);
            return;
          }
        }

        if (updatedAdditionalOffersNew.upsell?.enabled && updatedAdditionalOffersNew.upsell.products?.length > 0) {
          for (const product of updatedAdditionalOffersNew.upsell?.products) {
            if (!product.productName || !product.price || !product.description || (!product.image && !((product.url_images ?? []).length > 0))) {
              customToast(dictionary["upsell-information-incomplete"][language]);
              setIsLoading(false);
              return;
            }
          }
        }

        nextStep();
      } catch (err) {
        console.error('Error fetching product data:', err);
        customToast(dictionary["failed-to-fetch-product-data"][language], dictionary["failed-to-fetch-product-data-title"][language]);
      } finally {
        setIsLoading(false);
      }
    } else if (step === 3) {
      const newPriceErrors: { main?: string; bump?: string; upsell?: string } = {};

      if (!data.productData.price || !data.productData.price.trim() || parseFloat(data.productData.price) <= 0) {
        newPriceErrors.main = dictionary["price-required"][language];
      }
      if (data.additionalOffers.bumpOffer?.enabled) {
        if (!data.additionalOffers.bumpOffer?.price || !data.additionalOffers.bumpOffer?.price.trim() || parseFloat(data.additionalOffers.bumpOffer?.price) <= 0) {
          newPriceErrors.bump = dictionary["price-required"][language];
        }
      }
      if (data.additionalOffers.upsell?.enabled) {
        if (data.additionalOffers.upsell?.products?.some(product => !product.price || !product.price.trim() || parseFloat(product.price) <= 0)) {
          newPriceErrors.upsell = dictionary["price-required"][language];
        }
      }

      if (Object.keys(newPriceErrors).length > 0) {
        setPriceErrors(newPriceErrors);
        return;
      }

      clearPriceErrors();
      nextStep();
    } else if (step === 4) {
      // StepFunnelConfig calls onComplete → go to summary
      nextStep();
    } else {
      nextStep();
    }
  };

  // ─── Build shared products array ────────────────────────
  const buildProducts = (): MultiFunnelProduct[] | null => {
    if (parseFloat(data.productData.price) === 0) {
      customToast('The main product price cannot be 0.', 'Main Product Price Incomplete');
      return null;
    }

    const products: MultiFunnelProduct[] = [
      {
        external_id: data.productData.externalId || null,
        provider_id: data.productData.providerId || null,
        name: data.productData.name,
        description: data.productData.details,
        price: parseFloat(data.productData.price),
        url_images: data.visualContent.images || [],
        images: data.visualContent.imagesBase64 || [],
        type: 'PRINCIPAL',
        variants: data.productData.variants || null,
      },
    ];

    if (productCompleteness?.upsell?.enabled && productCompleteness.upsell.products?.length > 0) {
      for (const product of productCompleteness.upsell.products) {
        if (product.productName && product.price) {
          products.push({
            external_id: product.externalId || null,
            provider_id: product.providerId || null,
            name: product.productName,
            description: product.description || '',
            price: parseFloat(product.price) || 0,
            url_images: product.url_images || [],
            type: 'UP_SELL',
          });
        }
      }
    }

    if (productCompleteness?.bumpOffer?.enabled && productCompleteness.bumpOffer.productName && productCompleteness.bumpOffer.price) {
      products.push({
        external_id: productCompleteness.bumpOffer.externalId || null,
        provider_id: productCompleteness.bumpOffer.providerId || null,
        name: productCompleteness.bumpOffer.productName,
        description: productCompleteness.bumpOffer.description || '',
        price: parseFloat(productCompleteness.bumpOffer.price) || 0,
        url_images: productCompleteness.bumpOffer.url_images ? [productCompleteness.bumpOffer.url_images] : [],
        type: 'BUMP_OFFER',
      });
    }

    return products;
  };

  // ─── Unified submit: fires bulk + individual legacy in parallel ─────
  const handleSubmitMixed = async () => {
    if (!validateCredits()) return;

    setIsLoading(true);
    try {
      const products = buildProducts();
      if (!products) { setIsLoading(false); return; }

      const warrantyDays = data.productData.warranty === 'none' ? 0 :
        data.productData.warranty === 'custom' ?
          parseInt(data.productData.customWarrantyDays) : 30;

      const brandName = data.visualStyle.brandName || '';
      const allFunnels = multiFunnelData.funnels;
      const legacyCount = selectedLegacyTemplates.length;
      const newCount = allFunnels.length - legacyCount;

      // Split funnels: first newCount are AI templates, rest are legacy
      const newFunnels = allFunnels.slice(0, newCount);
      const legacyFunnels = allFunnels.slice(newCount);

      // ── Build unified funnel array (AI + legacy in one bulk call) ────────────
      let globalIndex = 1;
      const unifiedFunnels: MultiFunnelItem[] = [];

      // AI template funnels
      newFunnels.forEach(funnel => {
        const selectedTemplateDesign = funnel.designStyleSheet ? {
          theme: funnel.designTheme || funnel.designStyleSheet,
          primary_color: funnel.designPrimaryColor || '#FFFFFF',
          secondary_color: funnel.designSecondaryColor || '#FFFFFF',
          template_name: funnel.designTemplateName || funnel.designTemplateType || 'Template Studio',
          cover_style: funnel.designCoverStyle || 'template_studio_html',
          style_sheet: funnel.designStyleSheet,
          palette: funnel.designPalette || 'red',
          template_family: funnel.designTemplateType || 'sophisticated-html-v2',
        } : undefined;

        unifiedFunnels.push({
          brand_name: `${brandName} ${globalIndex++}`,
          sale_angles: funnel.selectedAngles,
          free_shipping: data.productData.freeShipping,
          warranties: warrantyDays,
          promotion: getSubmittablePromotion(funnel.promotion),
          ...(selectedTemplateDesign ? { template_design: selectedTemplateDesign } : {}),
          template_structure: {
            template_pages: buildTemplateStudioPages(funnel),
          },
          is_custom_sections_only: true,
        });
      });

      // Legacy template funnels
      legacyFunnels.forEach((funnel, idx) => {
        const legacyTemplate = selectedLegacyTemplates[idx];
        const saleAngles = funnel?.selectedAngles?.length > 0
          ? funnel.selectedAngles
          : data.visualContent.selectedAngle;

        unifiedFunnels.push({
          brand_name: `${brandName} ${globalIndex++}`,
          sale_angles: saleAngles,
          free_shipping: data.productData.freeShipping,
          warranties: warrantyDays,
          promotion: getSubmittablePromotion(funnel.promotion),
          template_design: {
            theme: legacyTemplate.styleSheet,
            primary_color: data.visualStyle.colorPalette || '#FFFFFF',
            secondary_color: '#FFFFFF',
            template_name: legacyTemplate.name,
            cover_style: legacyTemplate.coverStyle,
            style_sheet: legacyTemplate.styleSheet,
            palette: legacyTemplate.palette,
            template_family: 'sophisticated-html-v2',
          },
          is_custom_sections_only: false,
        });
      });

      // Single bulk call for ALL funnels (1 product, 1 base image, sequential processing)
      const bulkPayload: MultiFunnelPayload = {
        products,
        funnels: unifiedFunnels,
        data_for_context: {
          pain_detection: contextData.pain_detection,
          buyer_detection: contextData.buyer_detection,
          recommended_sales_angles: contextData.recommended_sales_angles,
        },
      };

      const bulkResponse = await postBulkFunnels({
        ...bulkPayload,
        client_request_id: buildBulkClientRequestId(onboardingRequestSessionId, bulkPayload),
      });

      // Fire async creative generation for AI funnels that have ad templates
      const { adTemplates, sectionTemplates } = useOnboardingStore.getState();
      for (let i = 0; i < newFunnels.length; i++) {
        const funnel = newFunnels[i];
        const selectedTemplateIds = funnel.selectedAdTemplates || [];
        if (selectedTemplateIds.length === 0) continue;

        const websiteId = bulkResponse[i]?.id;
        if (!websiteId) continue;

        const matchingSectionTemplate = sectionTemplates.find(
          t => t.type === funnel.designTemplateType && t.section_type === 'preview'
        );
        const primaryColor = (matchingSectionTemplate?.metadata as any)?.primary_color ?? null;
        const backgroundColor = (matchingSectionTemplate?.metadata as any)?.background_color ?? null;

        const selectedAdStyles = selectedTemplateIds
          .map(id => adTemplates.find(t => t.id === id))
          .filter((t): t is NonNullable<typeof t> => !!t);

        const templates = selectedAdStyles.map(template => ({
          user_prompt: '',
          image_template: template.image_url || template.coverImage,
          format: '1:1' as const,
          template_id: template.name_id || '',
          color_mode: 'custom' as const,
          primary_color: primaryColor,
          background_color: backgroundColor,
          reference_type: 'template' as const,
          config: { language },
          ...(funnel.selectedAngles.length > 0
            ? { angles: funnel.selectedAngles[0].name }
            : {}),
        }));

        creativesService.generateCreativesAsync(websiteId, {
          product_name: data.productData.name,
          product_image: data.visualContent.images?.[0] || '',
          product_description: data.productData.details,
          templates,
        }).catch(err => {
          console.error(`Error generating creatives for funnel ${funnel.name}:`, err);
        });
      }

      // Navigate to funnels on success
      if (bulkResponse.length > 0) {
        invalidateWebsitesCache();
        resetOnboardingData();
        navigate('/funnels');
      } else {
        customToast('No se pudo crear ningún embudo. Por favor intenta de nuevo.', 'Error de creación');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error submitting funnels:', error);
      customToast('Hubo un error creando los embudos. Por favor intenta de nuevo.', 'Error de creación');
      setIsLoading(false);
    }
  };

  const hasTextValue = (value?: string | null) => Boolean(value?.trim());
  const hasOfferImage = (image?: string, urlImages?: string | string[]) => {
    if (hasTextValue(image)) return true;
    if (Array.isArray(urlImages)) return urlImages.some((url) => hasTextValue(url));
    return hasTextValue(urlImages);
  };
  const canContinueOffer = (offer: {
    enabled?: boolean;
    uploadedMethod?: UploadedMethodType;
    productLink?: string | null;
    productName?: string;
    description?: string;
    image?: string;
    url_images?: string | string[];
  }) => {
    if (!offer.enabled) return true;
    const hasResolvedProduct = hasTextValue(offer.productName) &&
      hasTextValue(offer.description) &&
      hasOfferImage(offer.image, offer.url_images);

    if (offer.uploadedMethod === UploadedMethodType.LINK) {
      return hasTextValue(offer.productLink) || hasResolvedProduct;
    }

    return hasResolvedProduct;
  };
  const canContinueAdditionalOffers = () => {
    const bumpOfferOk = canContinueOffer(data.additionalOffers.bumpOffer ?? { enabled: false });
    const upsell = data.additionalOffers.upsell;
    const upsellsOk = !upsell?.enabled || (
      upsell.products.length > 0 &&
      upsell.products.every((product) => canContinueOffer({ ...product, enabled: true }))
    );
    return bumpOfferOk && upsellsOk;
  };

  const stepIsDisabled = (step === 2 && !canContinueAdditionalOffers()) || (step === 3 && data.visualStyle.brandName === '');
  const isWaitingForAnglesOnAngleStep = step === 4 && funnelSubStep === 2 && isFetchingSalesAngles;
  const isButtonDisabled = stepIsDisabled || isLoading || isWaitingForAnglesOnAngleStep || isFetchingSalesAngleRecommendation || isFetchingDesignRecommendation;
  const isAngleLoadingStep = step === 4 && funnelSubStep === 2 && (isFetchingSalesAngles || isFetchingSalesAngleRecommendation || (!hasFetchedSalesAngles && salesAngles.length === 0));
  const isCompactAngleStep = step === 4 && funnelSubStep === 2 && (isAngleLoadingStep || multiFunnelData.funnels.length <= 2);
  const hasActivePromotions = multiFunnelData.funnels.some(funnel => funnel.promotion?.enabled);
  const stepFourMaxWidthClass = step === 4 && funnelSubStep === 2
    ? (hasActivePromotions ? 'max-w-[64rem]' : 'max-w-3xl')
    : 'max-w-5xl';
  const stepFourCardClass = isCompactAngleStep
    ? 'rounded-[40px] p-6 md:p-8 max-h-[calc(100vh-100px)] overflow-hidden flex flex-col'
    : 'rounded-[40px] p-6 md:p-8 h-[calc(100vh-100px)] max-h-[calc(100vh-100px)] overflow-hidden flex flex-col';

  return (
    <>
      {/* <TutorialSetup />
      <WelcomeModal
        isOpen={isWelcomeOpen}
        onStart={startTutorial}
        onClose={closeTutorial}
      />
      <TutorialTooltip
        step={tutorialStep}
        onNext={nextTutorialStep}
        onClose={closeTutorial}
        isActive={isTutorialActive}
      /> */}
      <Toaster position="top-right" />
      {isOpenBuyCreditsModal && (
        <ModalWrapper isOpen={isOpenBuyCreditsModal} onClose={() => setIsOpenBuyCreditsModal(false)}>
          <BuyCreditsModal
            onClose={() => setIsOpenBuyCreditsModal(false)}
            nextRenewalDate={endMembershipDate}
            language={language}
            openInNewTab={true}
            onCreditsRefreshed={handleCreditsRefreshed}
          />
        </ModalWrapper>
      )}
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4 relative">
        {/* Custom sections toggle removed — all users now go through the unified flow */}
        <div className="fixed top-[22px] right-[24px] z-30">
          <Chip
            text={dictionary["credits"][language]}
            value={userCredits.toString()}
            onClick={() => setIsOpenBuyCreditsModal(true)}
            loading={loadingCredits}
          />
        </div>
        <div className={`w-full ${step === 4 ? stepFourMaxWidthClass : (step === 1 && (showConfirmScrapedProduct || data.productData.isManualEntry)) ? 'max-w-4xl' : 'max-w-2xl'} transition-all duration-300`}>
          <div className={`bg-white shadow-xl ${step === 4 ? stepFourCardClass : (step === 1 && (showConfirmScrapedProduct || data.productData.isManualEntry)) ? 'rounded-[40px] p-6 md:p-8 h-[calc(100vh-100px)] flex flex-col' : 'rounded-[50px] p-8 md:p-12'}`}>
            {renderStepContent()}

          {!showDesignAnalysis && !(step === 1 && showConfirmScrapedProduct) && !(step === 2 && scrapedConfirmQueueStep2.length > 0) && step !== 4 && (
          <div className={`${step === 1 ? 'flex justify-end items-end' : 'flex justify-between items-center'} mt-8`}>
            {step > 1 && (
              <button
                onClick={() => {
                  if (step === 2) {
                    // Going back to step 1: if product was scraped, show confirm screen
                    // so user can edit before going further back
                    if (data.productData.name && data.visualContent.images?.length) {
                      setShowConfirmScrapedProduct(true);
                      previousStep();
                      return;
                    }
                  }
                  previousStep();
                }}
                className="flex items-center gap-2 px-6 py-3 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                {dictionary["back"][language]}
              </button>
            )}

            <div className="flex items-center justify-end gap-4">
              {isLastStep && (
                <div className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#F1F5F9] rounded-full text-xs text-[#64748B]">
                  <span className="font-semibold text-gray-600">{dictionary["cost"][language]}:</span>
                  <span className="font-bold text-gray-700">
                    {estimatedCost?.total_cost ?? (multiFunnelData.funnels.length * 85)} {dictionary["credits"][language]}
                  </span>
                </div>
              )}

              <button
                disabled={isButtonDisabled}
                onClick={isLastStep ? handleSubmitMixed : handleContinue}
                className={`${isButtonDisabled ? 'bg-[#E2E8F0]' : isLastStep ? 'bg-gradient-to-r from-fluxi to-[#2F6EDA] hover:from-[#2F6EDA] hover:to-[#2558B8] shadow-lg shadow-blue-200' : 'bg-fluxi hover:bg-fluxi-dark'} ml-auto flex items-center gap-2 px-7 py-2.5 text-sm text-white rounded-full font-medium transition-all`}
              >
                {isLoading ? <Loader size={20} color="#FFFFFF" /> : null}
                {isLastStep
                  ? (dictionary["generate"]?.[language] ?? "Generar")
                  : dictionary["continue"][language]
                }
                {!isLastStep && <ArrowRight className="w-5 h-5" />}
              </button>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

// ─── Design Analysis Loading Screen ──────────────────────────
function DesignAnalysisScreen({ language }: { language: Language }) {
  const [activeStep, setActiveStep] = useState(0);

  const analysisSteps = [
    {
      icon: Sparkles,
      label: dictionary["analyzing-product"]?.[language] ?? "Analizando tu producto...",
    },
    {
      icon: Palette,
      label: dictionary["selecting-design"]?.[language] ?? "Seleccionando el mejor diseño...",
    },
    {
      icon: LayoutGrid,
      label: dictionary["optimizing-sections"]?.[language] ?? "Optimizando las secciones...",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % analysisSteps.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      {/* Animated AI icon */}
      <div className="relative mb-8">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-fluxi to-fluxi flex items-center justify-center animate-pulse">
          <Sparkles className="w-10 h-10 text-white" />
        </div>
        <div className="absolute inset-0 w-20 h-20 rounded-full bg-gradient-to-br from-fluxi to-fluxi opacity-20 animate-ping" />
      </div>

      {/* Title */}
      <h2 className="text-xl md:text-2xl font-bold text-gray-900 text-center mb-2">
        {dictionary["fluxi-analyzing-title"]?.[language] ?? "Fluxi está analizando tu producto"}
      </h2>
      <p className="text-sm text-[#64748B] text-center mb-10 max-w-md">
        {dictionary["fluxi-analyzing-subtitle"]?.[language] ?? "Estamos encontrando el diseño y las secciones más efectivas para tu tipo de producto"}
      </p>

      {/* Animated steps */}
      <div className="w-full max-w-sm space-y-3">
        {analysisSteps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === activeStep;
          const isDone = index < activeStep;

          return (
            <div
              key={index}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-500 ${
                isActive
                  ? 'bg-[#F5F8FF] border border-fluxi/20 shadow-sm'
                  : isDone
                    ? 'bg-[#F0FDF4] border border-green-200'
                    : 'bg-[#F8FAFC] border border-transparent'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                  isActive
                    ? 'bg-fluxi text-white'
                    : isDone
                      ? 'bg-green-500 text-white'
                      : 'bg-[#E2E8F0] text-gray-400'
                }`}
              >
                {isDone ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <Icon className="w-4 h-4" />
                )}
              </div>
              <span
                className={`text-sm font-medium transition-all duration-500 ${
                  isActive ? 'text-fluxi' : isDone ? 'text-green-600' : 'text-gray-400'
                }`}
              >
                {step.label}
              </span>
              {isActive && (
                <div className="ml-auto">
                  <Loader size={16} color="#4368E5" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
